import type { ArenaAnchor, ReceiverTier } from "../simulation/types.js";

export type Vec2 = [number, number];
export type Vec3 = [number, number, number];
export type ArenaPieceKind = "box" | "plane" | "light" | "portal-anchor" | "receiver";
export type ArenaPieceGroup = "arena" | "chutes" | "chamber" | "furnace" | "anchors" | "receivers" | "dressing";
export type ChuteId = "left" | "center" | "right";

export interface ArenaLabelDefinition {
  text: string;
  offset: Vec3;
  scale: Vec2;
  preset: string;
}

export interface ArenaMotionDefinition {
  axis: "x" | "yaw";
  origin: number;
  amplitude: number;
  tickDivisor: number;
}

export interface ArenaChuteOutletDefinition {
  offset: Vec3;
  velocity: Vec3;
}

export interface ArenaPieceDefinition {
  id: string;
  name: string;
  group: ArenaPieceGroup;
  kind: ArenaPieceKind;
  required: boolean;
  preset: string;
  position: Vec3;
  rotation: Vec3;
  size: Vec2 | Vec3;
  collidable: boolean;
  visible: boolean;
  role?: ArenaAnchor | ReceiverTier;
  normal?: Vec3;
  safeDefault?: boolean;
  label?: ArenaLabelDefinition;
  motion?: ArenaMotionDefinition;
  phaseVisibility?: "left" | "right";
  outlet?: ArenaChuteOutletDefinition;
}

/**
 * Second fixed, world-upright camera view framing the Processing Chamber
 * (LOCKED-DESIGN 2.2 / 18.8 predicate #3). Authored data like the gameplay
 * camera; presentation-only — no simulation state derives from it. The
 * input-basis yaw is DERIVED from position→target (CameraRig.fixedViewYaw)
 * so the look direction and the movement basis can never disagree.
 */
export interface ChamberViewDefinition {
  position: Vec3;
  target: Vec3;
  fov: number;
}

export interface ArenaDefinition {
  schemaVersion: 1;
  id: string;
  camera: {
    yaw: number;
    offset: Vec3;
    lookHeight: number;
    northBias: number;
    followRate: number;
    lookahead: number;
    fov: number;
    chamber: ChamberViewDefinition;
  };
  spawn: Vec3;
  zones: {
    intake: { min: Vec3; max: Vec3 };
    purge: { min: Vec3; max: Vec3 };
    chamberThresholdZ: number;
  };
  pieces: ArenaPieceDefinition[];
}

export interface ArenaIssue {
  severity: "error" | "warning";
  code: string;
  path: string;
  message: string;
}

export interface ArenaValidation {
  errors: ArenaIssue[];
  warnings: ArenaIssue[];
}

/**
 * Interior height of the visual enclosure, floor top → ceiling deck. Single
 * owner: `GrayBoxArena.buildEnclosure` hangs every overhead piece off it
 * (deck H+0.1, trusses H−0.4, lamp housings H−0.7 — LOCKED-DESIGN 18.13) and
 * `floorExtents.max[1]` reports it as the camera-containment ceiling, so the
 * shell the player sees and the box the camera may occupy can never disagree.
 */
export const ENCLOSURE_INTERIOR_HEIGHT = 19;

export interface ArenaRuntimeGeometry {
  spawn: { x: number; y: number; z: number };
  camera: ArenaDefinition["camera"];
  intake: ArenaDefinition["zones"]["intake"];
  purgeZone: ArenaDefinition["zones"]["purge"];
  islandOccluder: { min: Vec3; max: Vec3 };
  /**
   * Playable interior box: the authored `arena-floor` footprint (position ±
   * size/2) from its top face up to `ENCLOSURE_INTERIOR_HEIGHT` — the same
   * "shell extents DERIVE from the authored floor plan" precedent the
   * enclosure mesh already follows. The camera containment clamp (F6) reads
   * this, so re-authoring the floor re-sizes the camera box for free.
   */
  floorExtents: { min: Vec3; max: Vec3 };
  chamberThresholdZ: number;
  /**
   * Ceiling of the SEALED chamber volume: the UNDERSIDE of the authored
   * `chamber-ceiling` slab (position − size/2), derived for the same reason
   * `floorExtents` is — re-authoring the slab moves every "is this body in the
   * chamber?" test with it instead of leaving a constant behind.
   *
   * The chamber is a closed box (glass bulkhead + side glass at x ±12 + this
   * lid) inside a 19 m-tall room, so the footprint alone is NOT occupancy: the
   * open air and the lid's top surface above it satisfy z < `chamberThresholdZ`
   * and |x| ≤ 12 just as well as the interior does, and a body that comes to
   * rest up there is out of the furnace maw's reach and out of the player's
   * (PLAYTEST F19). The underside is the right face to bound with: no body
   * inside the box can have its centre above it, and no body resting on the roof
   * can have its centre below it — the two bands are a trash half-extent clear
   * of it in both directions.
   */
  chamberCeilingY: number;
  chuteOutlets: Record<ChuteId, { position: Vec3; velocity: Vec3 }>;
  defaultPortals: {
    A: { position: Vec3; normal: Vec3 };
    B: { position: Vec3; normal: Vec3 };
  };
}

export const ARENA_PLAYTEST_KEY = "trash-panic:arena-editor:playtest:v1";

export const ARENA_PRESETS_BY_KIND: Record<ArenaPieceKind, readonly string[]> = {
  box: ["floor", "wall", "platform", "machine", "conveyor", "purge", "glass", "glass-frame", "chamber", "furnace", "shutter", "safe-lane", "technical-lane", "hazard-lane", "blue-guide", "safe-guide", "technical-guide", "hazard-guide", "hazard-stripe", "hazard-baffle", "return-pad", "recovery-handle"],
  plane: ["fire"],
  light: ["fire"],
  "portal-anchor": ["blue"],
  receiver: ["safe", "technical", "hazard"],
};

const REQUIRED_ROLES = [
  "anchor-floor", "anchor-wall", "anchor-ramp", "anchor-island", "anchor-precision", "anchor-rail",
  "receiver-safe", "receiver-technical", "receiver-hazard", "sorting-island", "glass-bulkhead",
  "furnace-fire", "purge-station", "shutter-left", "shutter-right", "chute-left", "chute-center", "chute-right",
  "hazard-baffle",
] as const;
const PIECE_GROUPS: readonly ArenaPieceGroup[] = ["arena", "chutes", "chamber", "furnace", "anchors", "receivers", "dressing"];
const ANCHOR_ROLES: readonly ArenaAnchor[] = ["floor", "wall", "ramp", "island", "precision", "rail"];
const RECEIVER_ROLES: readonly ReceiverTier[] = ["safe", "technical", "hazard"];
const DEFAULT_CHUTE_OUTLET: ArenaChuteOutletDefinition = { offset: [0, -1.15, -.85], velocity: [0, -.4, -3.2] };
/**
 * Migration backfill for pre-chamber-view definitions: an interior corner
 * pose looking down the chamber gallery that keeps the receiver lanes, reject
 * area, return pad, recovery handle, and furnace maw in frame. Re-authored for
 * the 24-wide chamber block (LOCKED-DESIGN 18.13): an interior SE-corner
 * gallery pose looking WNW so the maw, all three receivers, and the return
 * pad frame from inside the shrunken shell (target y 0.3 pitches the view
 * down far enough that the low return pad clears the bottom frame margin).
 */
// Low east mid-band, UNDER the hazard lane slab (y 3.7–4.0) and its baffle
// (y ≈ 2.8+): the chamber interior is compartmentalized by lane furniture
// (lane-technical is a 3.6-tall wall, lane-hazard an eye-level slab), so any
// eye-height pose stares into a housing — the seventh-dogfood "maroon wall".
// From here the westward sightline clears every fixture; the chamber QA
// raycasts this (occlusion contract), so move it only with that test green.
const DEFAULT_CHAMBER_VIEW: ChamberViewDefinition = { position: [10.7, 2.6, -15.4], target: [-4, .9, -17.4], fov: 54 };

/**
 * Single classification seam for physical drop chutes (LOCKED-DESIGN 18.8).
 * The visual builder (GrayBoxArena) and the collider builder (ArenaPhysics)
 * must dispatch hollow-shell construction on this one predicate so an id edit
 * can never render an open chute over a solid simulation collider or vice versa.
 */
export function isChutePieceId(id: string): boolean {
  return /^chute-(left|center|right)$/.test(id);
}

/** Piece-level form of the chute seam for post-parse dispatch sites. */
export function isChutePiece(piece: ArenaPieceDefinition): boolean {
  return piece.kind === "box" && isChutePieceId(piece.id);
}

export function parseArenaDefinition(input: unknown): ArenaDefinition {
  const migrated = migrateArenaDefinition(input);
  const validation = validateArenaDefinition(migrated);
  if (validation.errors.length > 0) {
    throw new Error(validation.errors.map((issue) => `${issue.path}: ${issue.message}`).join("\n"));
  }
  return normalizeArenaDefinition(migrated as ArenaDefinition);
}

export function migrateArenaDefinition(input: unknown): unknown {
  if (!isRecord(input) || (input["schemaVersion"] !== 0 && input["schemaVersion"] !== 1)) return input;
  const migrated = structuredClone(input);
  if (migrated["schemaVersion"] === 0) {
    migrated["schemaVersion"] = 1;
    const camera = migrated["camera"];
    if (isRecord(camera) && camera["northBias"] === undefined && finite(camera["followBias"])) camera["northBias"] = camera["followBias"];
    if (isRecord(camera)) delete camera["followBias"];
  }
  if (Array.isArray(migrated["pieces"])) {
    for (const piece of migrated["pieces"]) {
      if (isRecord(piece) && typeof piece["id"] === "string" && isChutePieceId(piece["id"]) && piece["outlet"] === undefined) {
        piece["outlet"] = structuredClone(DEFAULT_CHUTE_OUTLET);
      }
    }
  }
  const camera = migrated["camera"];
  if (isRecord(camera) && camera["chamber"] === undefined) camera["chamber"] = structuredClone(DEFAULT_CHAMBER_VIEW);
  return migrated;
}

export function normalizeArenaDefinition(input: ArenaDefinition): ArenaDefinition {
  const clone = structuredClone(input);
  clone.pieces.sort((a, b) => a.id.localeCompare(b.id));
  normalizeNumbers(clone);
  return clone;
}

export function serializeArenaDefinition(input: ArenaDefinition): string {
  return `${JSON.stringify(normalizeArenaDefinition(input), null, 2)}\n`;
}

export function validateArenaDefinition(input: unknown): ArenaValidation {
  const issues: ArenaIssue[] = [];
  if (!isRecord(input)) return splitIssues([issue("error", "definition-invalid", "$", "Arena definition must be an object.")]);
  if (input["schemaVersion"] !== 1) issues.push(issue("error", "schema-version-unsupported", "schemaVersion", "Only arena schema version 1 is supported."));
  if (!Array.isArray(input["pieces"])) return splitIssues([...issues, issue("error", "pieces-invalid", "pieces", "Pieces must be an array.")]);

  const pieces = input["pieces"] as unknown[];
  const ids = new Set<string>();
  for (let index = 0; index < pieces.length; index++) {
    const value = pieces[index];
    const path = `pieces[${index}]`;
    if (!isRecord(value)) { issues.push(issue("error", "piece-invalid", path, "Piece must be an object.")); continue; }
    const id = value["id"];
    if (typeof id !== "string" || id.length === 0) issues.push(issue("error", "piece-id-invalid", `${path}.id`, "Piece ID must be a non-empty string."));
    else if (ids.has(id)) issues.push(issue("error", "piece-id-duplicate", `${path}.id`, `Piece ID ${id} is duplicated.`));
    else ids.add(id);
    if (typeof value["name"] !== "string" || value["name"].length === 0) issues.push(issue("error", "piece-name-invalid", `${path}.name`, "Piece name must be a non-empty string."));
    if (typeof value["group"] !== "string" || !PIECE_GROUPS.includes(value["group"] as ArenaPieceGroup)) issues.push(issue("error", "piece-group-invalid", `${path}.group`, "Piece group is not supported."));
    for (const field of ["required", "collidable", "visible"] as const) if (typeof value[field] !== "boolean") issues.push(issue("error", `piece-${field}-invalid`, `${path}.${field}`, `${field} must be boolean.`));
    if (!isVec(value["position"], 3)) issues.push(issue("error", "piece-position-invalid", `${path}.position`, "Position must contain three finite numbers."));
    if (!isVec(value["rotation"], 3)) issues.push(issue("error", "piece-rotation-invalid", `${path}.rotation`, "Rotation must contain three finite numbers."));
    if (!isVec(value["size"], 2) && !isVec(value["size"], 3)) issues.push(issue("error", "piece-size-invalid", `${path}.size`, "Size must contain two or three positive finite numbers."));
    else if ((value["size"] as number[]).some((number) => number <= 0)) issues.push(issue("error", "piece-size-invalid", `${path}.size`, "Every size dimension must be greater than zero."));
    const kind = value["kind"];
    const preset = value["preset"];
    if (typeof kind !== "string" || !(kind in ARENA_PRESETS_BY_KIND)) issues.push(issue("error", "piece-kind-invalid", `${path}.kind`, "Piece kind is not supported."));
    else if (typeof preset !== "string" || !ARENA_PRESETS_BY_KIND[kind as ArenaPieceKind].includes(preset)) issues.push(issue("error", "piece-preset-invalid", `${path}.preset`, `Preset ${String(preset)} is not valid for ${kind}.`));
    if (kind === "portal-anchor") {
      if (!isVec(value["normal"], 3)) issues.push(issue("error", "piece-normal-invalid", `${path}.normal`, "Portal anchors require a three-number surface normal."));
      if (!ANCHOR_ROLES.includes(value["role"] as ArenaAnchor)) issues.push(issue("error", "piece-role-invalid", `${path}.role`, "Portal anchor role is not supported."));
    }
    if (kind === "receiver") {
      if (!isVec(value["normal"], 3)) issues.push(issue("error", "piece-normal-invalid", `${path}.normal`, "Receivers require a three-number surface normal."));
      if (!RECEIVER_ROLES.includes(value["role"] as ReceiverTier)) issues.push(issue("error", "piece-role-invalid", `${path}.role`, "Receiver tier is not supported."));
    }
    const label = value["label"];
    if (label !== undefined && (!isRecord(label) || typeof label["text"] !== "string" || !isVec(label["offset"], 3) || !isVec(label["scale"], 2) || typeof label["preset"] !== "string")) {
      issues.push(issue("error", "piece-label-invalid", `${path}.label`, "Label requires text, a three-number offset, a two-number scale, and a preset."));
    }
    const motion = value["motion"];
    if (motion !== undefined && (!isRecord(motion) || (motion["axis"] !== "x" && motion["axis"] !== "yaw") || !finite(motion["origin"]) || !finite(motion["amplitude"]) || motion["amplitude"] <= 0 || !finite(motion["tickDivisor"]) || motion["tickDivisor"] <= 0)) {
      issues.push(issue("error", "piece-motion-invalid", `${path}.motion`, "Motion requires an x or yaw axis, a finite origin, and positive amplitude and tickDivisor."));
    }
    // The simulation kernel derives the hazard-baffle gate cycle from this
    // authored field (LOCKED-DESIGN 3.2); it must exist and rotate in yaw.
    if (id === "hazard-baffle" && (!isRecord(motion) || motion["axis"] !== "yaw")) {
      issues.push(issue("error", "baffle-motion-missing", `${path}.motion`, "The hazard baffle requires an authored yaw motion; the simulation derives its timing gate from it."));
    }
    if (typeof id === "string" && isChutePieceId(id)) {
      if (kind !== "box" || !isVec(value["size"], 3)) issues.push(issue("error", "chute-shape-invalid", path, "Physical drop chutes must be three-dimensional boxes."));
      const outlet = value["outlet"];
      if (!isRecord(outlet) || !isVec(outlet["offset"], 3) || !isVec(outlet["velocity"], 3)) issues.push(issue("error", "chute-outlet-invalid", `${path}.outlet`, "Physical drop chutes require a local outlet offset and launch velocity."));
    }
  }
  for (const id of REQUIRED_ROLES) {
    if (!ids.has(id)) issues.push(issue("error", "required-piece-missing", "pieces", `Required semantic piece ${id} is missing.`));
  }

  if (!isVec(input["spawn"], 3)) issues.push(issue("error", "spawn-invalid", "spawn", "Spawn must contain three finite numbers."));
  const camera = input["camera"];
  if (!isRecord(camera) || !isVec(camera["offset"], 3) || !["yaw", "fov", "lookHeight", "northBias", "followRate", "lookahead"].every((field) => finite(camera[field])) || (finite(camera["fov"]) && (camera["fov"] <= 20 || camera["fov"] >= 100))) {
    issues.push(issue("error", "camera-invalid", "camera", "Camera requires finite authored values, a three-number offset, and an FOV between 20 and 100 degrees."));
  }
  if (isRecord(camera) && !validChamberView(camera["chamber"])) {
    issues.push(issue("error", "camera-chamber-invalid", "camera.chamber", "The chamber view requires a position, a ground-plane-distinct target, and an FOV between 20 and 100 degrees."));
  }
  const zones = input["zones"];
  if (!isRecord(zones) || !validAabb(zones["intake"]) || !validAabb(zones["purge"]) || !finite(zones["chamberThresholdZ"])) {
    issues.push(issue("error", "zones-invalid", "zones", "Intake and purge zones must be valid min/max boxes."));
  }

  const pieceRecords = pieces.filter(isRecord);
  const furnace = pieceRecords.find((piece) => piece["id"] === "furnace-fire");
  if (furnace && isVec(furnace["position"], 3) && furnace["position"][2] > -8) {
    issues.push(issue("warning", "furnace-not-opposite-chutes", "pieces[furnace-fire].position", "The furnace should remain on the wall opposite the chutes."));
  }
  const island = pieceRecords.find((piece) => piece["id"] === "sorting-island");
  if (island && isVec(island["size"], 3) && island["size"][1] > 1.8) {
    issues.push(issue("warning", "island-not-waist-high", "pieces[sorting-island].size", "The sorting island exceeds the locked waist-high profile."));
  }
  const glass = pieceRecords.find((piece) => piece["id"] === "glass-bulkhead");
  if (glass && isVec(glass["size"], 3) && glass["size"][0] < 18) {
    issues.push(issue("warning", "bulkhead-not-broad", "pieces[glass-bulkhead].size", "The reinforced-glass bulkhead is no longer broad enough to expose the chamber."));
  }
  const safeAnchors = pieceRecords.filter((piece) => piece["kind"] === "portal-anchor" && piece["safeDefault"] === true);
  const safeReceivers = pieceRecords.filter((piece) => piece["kind"] === "receiver" && piece["safeDefault"] === true);
  if (safeAnchors.length !== 1) issues.push(issue("error", "safe-anchor-count", "pieces", "Exactly one portal anchor must be the safe default."));
  if (safeReceivers.length !== 1) issues.push(issue("error", "safe-receiver-count", "pieces", "Exactly one receiver must be the safe default."));
  return splitIssues(issues);
}

export function autoPlaceArenaLabel(input: ArenaDefinition, id: string): ArenaDefinition {
  const definition = parseArenaDefinition(input);
  const piece = requirePiece(definition, id);
  if (!piece.label) throw new Error(`Arena piece ${id} has no semantic label.`);
  const right: Vec3 = [Math.cos(definition.camera.yaw), 0, -Math.sin(definition.camera.yaw)];
  const screenSide = piece.position[0] * right[0] + piece.position[2] * right[2];
  const side = screenSide > 0 ? -1 : 1;
  const magnitude = Math.max(.75, piece.size[0] * .18);
  piece.label.offset = [stable(right[0] * side * magnitude), stable(piece.size[1] / 2 + .45), stable(right[2] * side * magnitude)];
  return normalizeArenaDefinition(definition);
}

export function deriveArenaGeometry(input: ArenaDefinition): ArenaRuntimeGeometry {
  const definition = parseArenaDefinition(input);
  const island = requirePiece(definition, "sorting-island");
  const blue = definition.pieces.find((piece) => piece.kind === "portal-anchor" && piece.safeDefault);
  const orange = definition.pieces.find((piece) => piece.kind === "receiver" && piece.safeDefault);
  const chuteOutlets = Object.fromEntries((["left", "center", "right"] as const).map((role) => {
    const chute = requirePiece(definition, `chute-${role}`);
    if (!chute.outlet) throw new Error(`Physical drop chute ${role} has no authored outlet.`);
    const position = rotateVector(chute.outlet.offset, chute.rotation).map((value, index) => stable(value + chute.position[index])) as Vec3;
    const velocity = rotateVector(chute.outlet.velocity, chute.rotation).map(stable) as Vec3;
    return [role, { position, velocity }];
  })) as ArenaRuntimeGeometry["chuteOutlets"];
  if (!blue?.normal || !orange?.normal) throw new Error("Safe default portal targets require normals.");
  const islandSize = island.size as Vec3;
  const floor = requirePiece(definition, "arena-floor");
  const floorSize = floor.size as Vec3;
  // Same `requirePiece` contract the floor already carries: the chamber lid is a
  // structural piece of the room, not dressing — without it the chamber is not a
  // sealed box and every occupancy test downstream is meaningless.
  const chamberCeiling = requirePiece(definition, "chamber-ceiling");
  const chamberCeilingSize = chamberCeiling.size as Vec3;
  return {
    spawn: { x: definition.spawn[0], y: definition.spawn[1], z: definition.spawn[2] },
    camera: structuredClone(definition.camera),
    intake: structuredClone(definition.zones.intake),
    purgeZone: structuredClone(definition.zones.purge),
    islandOccluder: {
      min: [stable(island.position[0] - islandSize[0] / 2), 0, stable(island.position[2] - islandSize[2] / 2)],
      max: [stable(island.position[0] + islandSize[0] / 2), stable(island.position[1] + islandSize[1] / 2), stable(island.position[2] + islandSize[2] / 2)],
    },
    floorExtents: {
      min: [stable(floor.position[0] - floorSize[0] / 2), stable(floor.position[1] + floorSize[1] / 2), stable(floor.position[2] - floorSize[2] / 2)],
      max: [stable(floor.position[0] + floorSize[0] / 2), ENCLOSURE_INTERIOR_HEIGHT, stable(floor.position[2] + floorSize[2] / 2)],
    },
    chamberThresholdZ: definition.zones.chamberThresholdZ,
    chamberCeilingY: stable(chamberCeiling.position[1] - chamberCeilingSize[1] / 2),
    chuteOutlets,
    defaultPortals: {
      A: { position: [...blue.position], normal: [...blue.normal] },
      B: { position: [...orange.position], normal: [...orange.normal] },
    },
  };
}

export function arenaPiece(input: ArenaDefinition, id: string): ArenaPieceDefinition {
  return requirePiece(parseArenaDefinition(input), id);
}

export function resolveRuntimeArenaDefinition(committed: ArenaDefinition): ArenaDefinition {
  if (!import.meta.env.DEV || typeof window === "undefined" || !new URLSearchParams(window.location.search).has("arenaDraft")) return parseArenaDefinition(committed);
  const raw = window.sessionStorage.getItem(ARENA_PLAYTEST_KEY);
  if (!raw) return parseArenaDefinition(committed);
  try { return parseArenaDefinition(JSON.parse(raw)); }
  catch (error) { console.error("Invalid arena editor playtest draft; using canonical arena.", error); return parseArenaDefinition(committed); }
}

function requirePiece(definition: ArenaDefinition, id: string): ArenaPieceDefinition {
  const piece = definition.pieces.find((candidate) => candidate.id === id);
  if (!piece) throw new Error(`Required arena piece ${id} is missing.`);
  return piece;
}

function normalizeNumbers(value: unknown): void {
  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index++) {
      if (typeof value[index] === "number") value[index] = Object.is(value[index], -0) ? 0 : Number(value[index].toFixed(6));
      else normalizeNumbers(value[index]);
    }
  } else if (isRecord(value)) {
    for (const key of Object.keys(value)) {
      if (typeof value[key] === "number") value[key] = Object.is(value[key], -0) ? 0 : Number((value[key] as number).toFixed(6));
      else normalizeNumbers(value[key]);
    }
  }
}

/**
 * The chamber view derives its input-basis yaw from position→target on the
 * ground plane; coincident ground coordinates would make that yaw undefined,
 * so they are rejected here rather than guarded at runtime.
 */
function validChamberView(value: unknown): boolean {
  if (!isRecord(value) || !isVec(value["position"], 3) || !isVec(value["target"], 3)) return false;
  if (!finite(value["fov"]) || value["fov"] <= 20 || value["fov"] >= 100) return false;
  const position = value["position"] as number[];
  const target = value["target"] as number[];
  return Math.hypot(target[0]! - position[0]!, target[2]! - position[2]!) > 1e-6;
}

function validAabb(value: unknown): boolean {
  return isRecord(value) && isVec(value["min"], 3) && isVec(value["max"], 3) && value["min"].every((min, index) => min < value["max"][index]!);
}
function isVec(value: unknown, length: number): value is number[] { return Array.isArray(value) && value.length === length && value.every(finite); }
function finite(value: unknown): value is number { return typeof value === "number" && Number.isFinite(value); }
function stable(value: number): number {
  if (Math.abs(value) < 1e-5) return 0;
  const rounded = Number(value.toFixed(6));
  return Object.is(rounded, -0) ? 0 : rounded;
}
function rotateVector([x, y, z]: Vec3, [rx, ry, rz]: Vec3): Vec3 {
  const cx = Math.cos(rx); const sx = Math.sin(rx); const cy = Math.cos(ry); const sy = Math.sin(ry); const cz = Math.cos(rz); const sz = Math.sin(rz);
  const afterX: Vec3 = [x, y * cx - z * sx, y * sx + z * cx];
  const afterY: Vec3 = [afterX[0] * cy + afterX[2] * sy, afterX[1], -afterX[0] * sy + afterX[2] * cy];
  return [afterY[0] * cz - afterY[1] * sz, afterY[0] * sz + afterY[1] * cz, afterY[2]];
}
function isRecord(value: unknown): value is Record<string, any> { return typeof value === "object" && value !== null && !Array.isArray(value); }
function issue(severity: ArenaIssue["severity"], code: string, path: string, message: string): ArenaIssue { return { severity, code, path, message }; }
function splitIssues(issues: ArenaIssue[]): ArenaValidation { return { errors: issues.filter((item) => item.severity === "error"), warnings: issues.filter((item) => item.severity === "warning") }; }
